import React, { useState, useEffect, useReducer, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import IconButton from "@material-ui/core/IconButton";
import VisibilityIcon from "@material-ui/icons/Visibility";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ChatiaDialog from "../../components/ChatiaDialog";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";

const reducer = (state, action) => {
  if (action.type === "LOAD_AFFILIATES") {
    const affiliates = action.payload;
    const newAffiliates = [];
    affiliates.forEach((a) => {
      const idx = state.findIndex((s) => s.affiliateCode === a.affiliateCode);
      if (idx !== -1) {
        state[idx] = a;
      } else {
        newAffiliates.push(a);
      }
    });
    return [...state, ...newAffiliates];
  }
  if (action.type === "RESET") {
    return [];
  }
  return state;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3),
  },
  statsCard: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  statItem: {
    flex: 1,
    minWidth: 140,
    padding: theme.spacing(2),
    textAlign: "center",
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
  },
  statValue: {
    fontSize: "1.6rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  statLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
    marginTop: 2,
  },
}));

const eventLabels = {
  purchase_approved: "Compra Aprovada",
  refund: "Reembolso",
  subscription_cancellation: "Cancelamento",
  chargeback: "Chargeback",
};

const eventColors = {
  purchase_approved: "#4caf50",
  refund: "#ffffff",
  subscription_cancellation: "#ff9800",
  chargeback: "#ffffff",
};

const Affiliates = () => {
  const classes = useStyles();
  const { user: loggedInUser } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [affiliates, dispatch] = useReducer(reducer, []);
  const [affiliateDetail, setAffiliateDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const { data } = await api.get("/affiliates", {
          params: { searchParam, pageNumber },
        });
        dispatch({ type: "LOAD_AFFILIATES", payload: data.affiliates });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };
    fetchData();
  }, [searchParam, pageNumber]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setLoadingMore(true);
      setPageNumber((prev) => prev + 1);
    }
  };

  const handleViewDetail = async (affiliate) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const { data } = await api.get(`/affiliates/${encodeURIComponent(affiliate.affiliateCode)}`);
      setAffiliateDetail(data);
    } catch (err) {
      toastError(err);
    } finally {
      setDetailLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "R$ 0,00";
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("pt-BR");
  };

  if (!loggedInUser?.super) {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Afiliados ({affiliates.length})</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder="Buscar por nome ou código do afiliado..."
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">Código</TableCell>
              <TableCell align="center">Nome</TableCell>
              <TableCell align="center">Total Vendas</TableCell>
              <TableCell align="center">Valor Total</TableCell>
              <TableCell align="center">Última Venda</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {affiliates.map((a) => (
              <TableRow key={a.affiliateCode}>
                <TableCell align="center">
                  <Chip label={a.affiliateCode} size="small" color="primary" variant="outlined" />
                </TableCell>
                <TableCell align="center">{a.affiliateName || "-"}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={a.totalSales}
                    size="small"
                    style={{ backgroundColor: "#2196f3", color: "#fff", fontWeight: "bold" }}
                  />
                </TableCell>
                <TableCell align="center">{formatCurrency(a.totalAmount)}</TableCell>
                <TableCell align="center">{formatDate(a.lastSaleAt)}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleViewDetail(a)}>
                    <VisibilityIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loadingMore && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {loading && !loadingMore && (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        )}
      </Paper>

      {/* Dialog de detalhes do afiliado usando ChatiaDialog */}
      <ChatiaDialog
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setAffiliateDetail(null); }}
        title={`Afiliado: ${affiliateDetail?.affiliate?.affiliateName || affiliateDetail?.affiliate?.affiliateCode || "..."}`}
        maxWidth="lg"
        hideFooter
      >
        {detailLoading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : affiliateDetail ? (
          <>
            <div className={classes.statsCard}>
              <Paper className={classes.statItem} variant="outlined">
                <div className={classes.statValue}>{affiliateDetail.transactions?.length || 0}</div>
                <div className={classes.statLabel}>Total de Transações</div>
              </Paper>
              <Paper className={classes.statItem} variant="outlined">
                <div className={classes.statValue} style={{ color: "#4caf50" }}>
                  {affiliateDetail.transactions?.filter(t => t.event === "purchase_approved").length || 0}
                </div>
                <div className={classes.statLabel}>Vendas Aprovadas</div>
              </Paper>
              <Paper className={classes.statItem} variant="outlined">
                <div className={classes.statValue} style={{ color: "#ffffff" }}>
                  {affiliateDetail.transactions?.filter(t => t.event === "refund").length || 0}
                </div>
                <div className={classes.statLabel}>Reembolsos</div>
              </Paper>
              <Paper className={classes.statItem} variant="outlined">
                <div className={classes.statValue} style={{ color: "#2196f3" }}>
                  {formatCurrency(
                    affiliateDetail.transactions
                      ?.filter(t => t.event === "purchase_approved")
                      .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0)
                  )}
                </div>
                <div className={classes.statLabel}>Receita Total</div>
              </Paper>
            </div>

            <Typography variant="subtitle1" style={{ marginTop: 16, marginBottom: 8, fontWeight: "bold" }}>
              Histórico de Transações
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">Data</TableCell>
                  <TableCell align="center">Evento</TableCell>
                  <TableCell align="center">Cliente</TableCell>
                  <TableCell align="center">Produto</TableCell>
                  <TableCell align="center">Valor</TableCell>
                  <TableCell align="center">Plataforma</TableCell>
                  <TableCell align="center">Empresa</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {affiliateDetail.transactions?.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell align="center">{formatDate(t.createdAt)}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={eventLabels[t.event] || t.event}
                        size="small"
                        style={{
                          backgroundColor: eventColors[t.event] || "#9e9e9e",
                          color: "#fff",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">{t.buyerName || "-"}</TableCell>
                    <TableCell align="center">{t.productName || "-"}</TableCell>
                    <TableCell align="center">{formatCurrency(t.amount)}</TableCell>
                    <TableCell align="center">{t.platform?.toUpperCase()}</TableCell>
                    <TableCell align="center">
                      {t.company ? (
                        <Chip
                          label={`#${t.company.id} ${t.company.name}`}
                          size="small"
                          style={{
                            backgroundColor: t.company.status ? "#4caf50" : "#ffffff",
                            color: "#fff",
                          }}
                        />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        ) : null}
      </ChatiaDialog>
    </MainContainer>
  );
};

export default Affiliates;
